import {
  APP_INITIALIZER,
  EnvironmentProviders,
  PLATFORM_ID,
  Provider,
  makeEnvironmentProviders,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { environment } from 'src/environments/environment';
import { DatabaseService } from './database.service';
import { OfflinePreloadService } from './http/offline-preload.service';
import { OutboxService } from './http/outbox.service';
import { StorageService } from './storage.service';
import { SyncService } from './sync/sync.service';
import { MediaSyncService } from './sync/media-sync.service';

/**
 * Wires the offline layer into application bootstrap.
 *
 * Add to app.config.ts:
 *
 *   providers: [
 *     ...,
 *     provideOfflineDatabase(),
 *   ]
 *
 * and register `offlineInterceptor` with provideHttpClient - see app.config.ts.
 *
 * Startup is deliberately non-blocking and never throws. A browser that refuses
 * IndexedDB (private mode, enterprise policy, no quota) must still get a working
 * online app - degraded to network-only, not a blank screen. So every failure
 * here is logged and swallowed.
 */
export function provideOfflineDatabase(): EnvironmentProviders {
  const providers: Provider[] = [
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [
        PLATFORM_ID,
        DatabaseService,
        StorageService,
        SyncService,
        OutboxService,
        OfflinePreloadService,
        MediaSyncService,
      ],
      useFactory:
        (
          platformId: object,
          database: DatabaseService,
          storage: StorageService,
          sync: SyncService,
          outbox: OutboxService,
          preload: OfflinePreloadService,
          mediaSync: MediaSyncService,
        ) =>
        () => {
          // Server render: no IndexedDB, nothing to start.
          if (!isPlatformBrowser(platformId)) return Promise.resolve();

          // Registered before anything can sign in: at login, after the tables
          // are loaded, every teacher-assessment screen is prepared on this
          // device as the last stage of the loading page.
          sync.registerHydrationTask(preload.hydrationTask());
          sync.registerHydrationTask(mediaSync.hydrationTask());

          // Not awaited. Opening the database and the first sync must not sit
          // in front of the first paint - the UI reads through repositories,
          // which await `ready()` themselves.
          void (async () => {
            try {
              await database.ready();
              await storage.refresh();
              await sync.start();
              // Writes stored while the API was unreachable - including any
              // left in the retired localStorage queue - replay as soon as
              // there is a session and a connection.
              await outbox.start();

              // One console call to answer "why has it not synced?" - see
              // SyncService.diagnose. Development only, and it exposes nothing
              // a signed-in user could not already read from their own store.
              if (!environment.production) {
                (globalThis as unknown as Record<string, unknown>)['__syncDiagnostics'] =
                  () =>
                    sync.diagnose().then((report) => {
                      const full = {
                        ...report,
                        outboxPending: outbox.pendingCount(),
                        outboxRejected: outbox.rejectedCount(),
                      };
                      console.table(report['rowCounts']);
                      console.log(full);
                      return full;
                    });
              }
            } catch (error) {
              console.error(
                '[offline] disabled for this session; the app will work online only.',
                error,
              );
            }
          })();

          return Promise.resolve();
        },
    },
  ];

  return makeEnvironmentProviders(providers);
}
